import { useState, useEffect, useCallback } from 'react';
import { authClient } from './auth';

export interface UsageData {
  plan: {
    id: string;
    name: string;
    price: number;
    workspaceLimit?: number;
  };
  storage: {
    usedBytes: number;
    tenantUsedBytes?: number;
    reservedBytes: number;
    limitBytes: number;
  };
  members: {
    used: number;
    limit: number;
  };
  isPooled?: boolean;
  workspaceCount?: number;
  workspaceLimit?: number;
  masterTenantId?: string;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function useUsage() {
  const { data: activeOrg } = authClient.useActiveOrganization();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantId = activeOrg?.id;

  const fetchUsage = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/billing/usage?tenantId=${tenantId}`);
      if (!res.ok) {
        throw new Error('Failed to load usage');
      }
      const data = await res.json();
      setUsage(data);
    } catch (err: any) {
      setError(err.message || 'Error fetching usage');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    const handleRefresh = () => fetchUsage();
    window.addEventListener('usage:refresh', handleRefresh);
    return () => window.removeEventListener('usage:refresh', handleRefresh);
  }, [fetchUsage]);

  const usedBytes = usage?.storage.usedBytes ?? 0;
  const limitBytes = usage?.storage.limitBytes ?? (5 * 1024 * 1024 * 1024);
  const percent = limitBytes > 0 ? Math.min(100, Math.round((usedBytes / limitBytes) * 100)) : 0;

  const storageUsedStr = `${formatBytes(usedBytes)} / ${formatBytes(limitBytes)}`;

  return {
    usage,
    loading,
    error,
    refresh: fetchUsage,
    storagePercent: percent,
    storageUsedStr,
    isWarning: percent >= 80 && percent < 100,
    isBlocked: percent >= 100,
    planName: usage?.plan.name ?? 'Free',
  };
}
